import * as React from 'react';

import type { BannerType } from '@sunwebgroup/ui-components';
import {ServiceType} from "../../../domain/types";
import {SpaceBetween, tk, Typography, BulletList, Banner, Icon} from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import {useService} from '@/scopes/checkout/domain/hooks';
import ServiceBox from '../ServiceBox';
import { styled } from 'styled-components';
import { SelectedCarView } from './SelectedCarView';
import { CarsListView } from './CarsListView';
import { ExtraCarsView } from './ExtraCarsView';
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable';

export function CarRentalService() {
  const intl = useIntl();
  const { service } = useService(ServiceType.CarRental);
  useSendAncillaryAvailable(service, 'selectCarRental')
  if (!service) return null;

  const { usps, options, carLists } = service;
  const modalServiceDescription = `
    ${intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_modal_description' })}
    <ul>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_modal_description_list_item_1' })}</li>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_modal_description_list_item_2' })}</li>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_modal_description_list_item_3' })}</li>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_modal_description_list_item_4' })}</li>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_modal_description_list_item_4' })}</li>
    </ul>
    ${intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_modal_description_link' })}
  `
  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_description' })}
      glyph="IconCar"
      modalTitle={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_modal_title' })}
      modalDescription={modalServiceDescription}
    >
      <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
        <CarRentalUsps>
          {usps.map(({ link, text }) => (
            <BulletList.Item key={text} $icon="IconCheckmark">
              {link && <a href={link}>{text}</a>}
              {!link && text}
            </BulletList.Item>
          ))}
        </CarRentalUsps>

        <WarningMessages />

        {carLists && carLists?.length > 0 && (
          carLists?.map(({ selectedValue }: { selectedValue: string }, index) => {
            const selectedOption = options?.[selectedValue];
            if (!selectedOption) return null;
            return (
              <SpaceBetween direction='y' spacing={tk('spacing_xs')} key={selectedValue}>
                { service.isOpenJawPackage && selectedOption.openJawFeeAmount > 0 && <OpenJawMessage openJawFeeAmount={selectedOption.openJawFeeAmount} /> }
                <SelectedCarView selectedOption={selectedOption} />
                {service.maxNumbersOfCarsToBeBooked > carLists?.length && (carLists?.length === index + 1) && <ExtraCarsView {...service} />}
              </SpaceBetween>
            );
          })
        )}
        {carLists && carLists?.length === 0 && <CarsListView />}
      </SpaceBetween>
    </ServiceBox>
  );
}

function WarningMessages() {
  const intl = useIntl();
  const { service } = useService(ServiceType.CarRental);

  if (!service) return null;

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
      { service.includedCarOutOfStock && <Banner $type={'warning' as BannerType}>
          <Texts direction="y">
            <Typography $variant="label">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_included_car_not_available_title' })}</Typography>
            <Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_included_car_not_available_description' })}</Typography>
          </Texts>
        </Banner>
      }
      { service.includedCarPriceChanged && <Banner $type={'warning' as BannerType}>
          <Texts direction="y">
            <Typography $variant="label">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_included_car_price_changed_title' })}</Typography>
            <Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_included_car_price_changed_description' })}</Typography>
          </Texts>
        </Banner>
      }
    </SpaceBetween>
  );
}

function OpenJawMessage({ openJawFeeAmount }: { openJawFeeAmount: number }) {
  const intl = useIntl();
  return (
    <Banner $type={'info' as BannerType}>
      <Texts direction="y">
        <Typography $variant="label"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_open_jaw_car_title' })}</Typography>
        <Typography>
          {intl.formatMessage(
            { id: 'checkout_steps_ancillaries_car_rental_open_jaw_car_description' },
            { openJawFeeAmount:  intl.formatCurrency(openJawFeeAmount)}
          )}
        </Typography>
      </Texts>
    </Banner>
  );
}
const CarRentalUsps = styled(BulletList.Root)`
  justify-content: flex-start;
  display: flex;
  flex-wrap: wrap;
  ${BulletList.Item} {
    width: 100%;
    ${Icon} {
      color: ${tk('color_palette_green_500')};
    }
  }
  @media ${tk('mq_tablet')} {
    column-count: 3;
    column-gap: ${tk('spacing_lg')};
    display: inline-block;

    ${BulletList.Item} {
      display: flex;
      align-items: center;
      white-space: normal;
    }
  }
`;
const Texts = styled(SpaceBetween)`
  flex: 1;
`;
